// src/services/retargeting/fbPixel.service.ts

/**
 * Facebook Pixel Service
 * Centralized Facebook Pixel tracking with cookie consent support
 */

export enum FBPixelEventType {
    ASSISTANT_SELECTION = 'Assistant Selection',
    HIGH_INTENT_WORDS = 'High Intent Words',
    CHAT_SEND = 'Chat Send',
}

export interface FBPixelEventParams {
    value?: number;
    currency?: string;
    content_name?: string;
    content_category?: string;
    [key: string]: any;
}

declare global {
    interface Window {
        fbq: {
            (command: 'track', eventName: string, params?: any): void;
            (command: 'trackCustom', eventName: string, params?: any): void;
            (command: 'init', pixelId: string): void;
            (command: string, ...args: any[]): void;
            callMethod?: (...args: any[]) => void;
            queue?: any[];
            loaded?: boolean;
            version?: string;
        };
        _fbq?: any;
        fbqStub: (...args: any[]) => void;
        fbqQueue: any[];
    }
}

class FBPixelService {
    private static instance: FBPixelService;
    private pixelId = '2700354070295529';
    private isInitialized = false;

    private constructor() {}

    static getInstance(): FBPixelService {
        if (!FBPixelService.instance) {
            FBPixelService.instance = new FBPixelService();
        }
        return FBPixelService.instance;
    }

    /**
     * Check if user has consented to cookies
     */
    private getCookieConsent(): string | null {
        try {
            return localStorage.getItem('cookieConsent');
        } catch (error) {
            console.warn('localStorage not available:', error);
            return null;
        }
    }

    /**
     * Initialize Facebook Pixel
     */
    initialize(): void {
        console.log('Facebook Pixel initialize() called');

        if (this.isInitialized) {
            console.log('Facebook Pixel already initialized, skipping');
            return;
        }

        if (typeof window === 'undefined') {
            console.log('Window is undefined, skipping Facebook Pixel init');
            return;
        }

        const consent = this.getCookieConsent();
        console.log('Cookie consent status:', consent);

        if (consent === 'accepted') {
            console.log('Consent accepted, loading Facebook Pixel');
            this.loadFBPixel();
        } else {
            console.log('No consent yet, initializing stub');
            // Use stub until consent is given
            this.initializeStub();
            this.listenForConsent();
        }

        this.isInitialized = true;
        console.log('Facebook Pixel initialization complete');
    }

    /**
     * Initialize Facebook Pixel stub for queuing events before consent
     */
    private initializeStub(): void {
        window.fbqQueue = [];

        window.fbqStub = function(...args: any[]) {
            window.fbqQueue.push(args);
        };

        window.fbq = window.fbqStub as any;
    }

    /**
     * Load Facebook Pixel script and initialize tracking
     */
    private loadFBPixel(): void {
        // Facebook Pixel initialization code
        (function(f: any, b: any, e: string, v: string) {
            if (f.fbq) return;
            const n: any = f.fbq = function() {
                n.callMethod ?
                    n.callMethod.apply(n, arguments) : n.queue.push(arguments)
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = true;
            n.version = '2.0';
            n.queue = [];
            const t = b.createElement(e);
            t.async = true;
            t.src = v;
            const s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

        // Initialize pixel
        window.fbq('init', this.pixelId);
        window.fbq('track', 'PageView');

        // Process any queued events
        if (window.fbqQueue && window.fbqQueue.length > 0) {
            window.fbqQueue.forEach((args) => {
                window.fbq.apply(null, args);
            });
            window.fbqQueue = [];
        }

        console.log('Facebook Pixel script loaded and initialized');
    }

    /**
     * Listen for cookie consent events
     */
    private listenForConsent(): void {
        window.addEventListener('cookieConsentAccepted', () => {
            if (!window.fbq || window.fbq === window.fbqStub) {
                this.loadFBPixel();
            }
        });
    }

    /**
     * Track a custom event
     */
    trackCustomEvent(
        eventType: FBPixelEventType | string,
        params: FBPixelEventParams = {}
    ): void {
        const consent = this.getCookieConsent();

        if (consent !== 'accepted') {
            console.log('Facebook Pixel tracking skipped - cookies not accepted');
            return;
        }

        if (typeof window === 'undefined' || !window.fbq) {
            console.warn('Facebook Pixel not initialized');
            return;
        }

        try {
            const defaultParams: FBPixelEventParams = {
                value: 1.0,
                currency: 'CAD',
                ...params,
            };

            window.fbq('trackCustom', eventType, defaultParams);

            console.log('Facebook Pixel custom event tracked:', {
                event: eventType,
                params: defaultParams,
            });
        } catch (error) {
            console.error('Failed to track Facebook Pixel event:', error);
        }
    }

    /**
     * Track Assistant Selection event
     */
    trackAssistantSelection(assistantName: string, category?: string): void {
        this.trackCustomEvent(FBPixelEventType.ASSISTANT_SELECTION, {
            content_name: assistantName,
            content_category: category || 'uncategorized',
        });
    }

    /**
     * Track High Intent Words event
     */
    trackHighIntentWords(assistantName?: string): void {
        this.trackCustomEvent(FBPixelEventType.HIGH_INTENT_WORDS, {
            content_name: assistantName,
        });
    }

    /**
     * Track Chat Send event (first message only)
     */
    trackChatSend(assistantName?: string): void {
        this.trackCustomEvent(FBPixelEventType.CHAT_SEND, {
            content_name: assistantName,
        });
    }
}

// Export singleton instance
export const fbPixelService = FBPixelService.getInstance();